import React, { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { configureApiClient, configureUnauthorizedHandler } from '../api/apiClient';
import authConfig from './authConfig';

/**
 * FINAL FIX — window.name approach
 *
 * Previous approaches failed:
 * :x: loginWindow.sessionStorage.setItem() — blocked by browser
 * :x: #sso_login URL hash — stripped by server redirect before index.js runs
 *
 * window.name WORKS because:
 * :white_check_mark: Set when window.open(url, 'sso_login_window') is called
 * :white_check_mark: Persists through ALL page navigations in the same window
 * :white_check_mark: Survives WSO2 → Entra → /logincallback redirect chain
 * :white_check_mark: Readable anywhere in JS — no timing issues
 * :white_check_mark: No server involvement — purely browser-side
 *
 * FLOW:
 * 1. Popup opens → openLoginWindow() → window.open(url, 'sso_login_window')
 * 2. Login window loads → window.name = 'sso_login_window' :white_check_mark:
 * 3. isSsoLoginWindow() = true → signinRedirect() fires
 * 4. WSO2 → Entra → /logincallback
 *    window.name still = 'sso_login_window' :white_check_mark:
 * 5. onSigninCallback checks window.name → sso window detected
 *    → signals popup → closes window :white_check_mark:
 * 6. Popup localStorage poll detects sso_complete → reloads :white_check_mark:
 */

const SSO_WINDOW_NAME = 'sso_login_window';

function isInPopupOrIframe() {
  try {
    return window.opener !== null || window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Check if THIS window is the SSO login window.
 * window.name is set by window.open() and survives all redirects.
 */
function isSsoLoginWindow() {
  return window.name === SSO_WINDOW_NAME;
}

/**
 * Check if login window was already opened recently.
 * Uses localStorage so it persists across popup reloads.
 */
function isLoginWindowAlreadyOpen() {
  try {
    const ts = localStorage.getItem('sso_login_window_opened_at');
    if (!ts) return false;
    return Date.now() - parseInt(ts) < 180000; // 3 minutes
  } catch {
    return false;
  }
}

function markLoginWindowOpened() {
  try {
    localStorage.setItem('sso_login_window_opened_at', Date.now().toString());
  } catch {}
}

function clearLoginWindowOpened() {
  try {
    localStorage.removeItem('sso_login_window_opened_at');
  } catch {}
}

/**
 * Signal the popup that SSO is complete and close this login window.
 */
function sendSsoCompleteAndClose() {
  // sendSsoCompleteAndClose: signaling popup that SSO is complete (logs removed)

  // SIGNAL 1 — localStorage (most reliable — persists across popup reloads)
  try {
    localStorage.setItem('sso_complete', 'true');
  } catch (e) {
    // localStorage fallback failed (silently ignored)
  }

  // SIGNAL 2 — BroadcastChannel (instant if popup didn't reload)
  try {
    const channel = new BroadcastChannel('sso_channel');
    channel.postMessage('sso_complete');
    setTimeout(() => channel.close(), 2000);
  } catch (e) {
    // BroadcastChannel fallback failed (silently ignored)
  }

  // SIGNAL 3 — direct opener reload if available
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.location.reload();
    }
  } catch (e) {
    // opener reload failed (silently ignored)
  }

  clearLoginWindowOpened();

  // Close login window after signals sent
  setTimeout(() => { window.close(); }, 800);
}

/**
 * Open login window with name 'sso_login_window'.
 * window.name persists through all redirects in that window :white_check_mark:
 */
function openLoginWindow(url) {
  // Very small window anchored to bottom-right of screen
  const width = 280;
  const height = 340;
  const left = window.screen.width - width - 20;   // 20px from right
  const top  = window.screen.height - height - 60; // 60px from bottom (taskbar gap)

  const cleanUrl = url.split('#')[0];
  // openLoginWindow: opening SSO popup (logs removed)

  const win = window.open(
    cleanUrl,
    SSO_WINDOW_NAME,
    `width=${width},height=${height},left=${left},top=${top},` +
    `toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
  );

  if (win) {
    markLoginWindowOpened();

    // ── win.closed polling as retry trigger ────────────────────────────────
    // WHY: When the login window navigates to Microsoft (COOP: same-origin),
    // Chrome severs the browsing context group and `win` becomes a null proxy
    // that returns `closed=true` PREMATURELY (before auth is complete).
    // We debounce by 5 seconds to skip this false trigger.
    //
    // When win.closed fires for REAL (window.close() called after auth):
    //   - If postMessage already worked → storeUser() stored token → reload shows app ✅
    //   - If postMessage failed (first auth, COOP killed opener) → reload opens a
    //     NEW login window. This time WSO2 session exists → NO Microsoft redirect
    //     → NO COOP → window.opener is valid → postMessage works → storeUser ✅
    const openedAt = Date.now();
    const COOP_DEBOUNCE_MS = 6000; // COOP proxy fires in ~2-3s; 6s is safe margin

    const closedPoll = setInterval(() => {
      try {
        if (win.closed) {
          const age = Date.now() - openedAt;
          if (age < COOP_DEBOUNCE_MS) {
            // Likely a COOP null-proxy false-positive — ignore and keep polling
            return;
          }
          // Window genuinely closed (or COOP + enough time passed)
          clearInterval(closedPoll);
          clearTimeout(closedPollTimeout);
          // Login window closed — reloading popup
          clearLoginWindowOpened();
          window.location.reload();
        }
      } catch (e) {
        // Accessing win.closed threw — unusual, treat as closed
        clearInterval(closedPoll);
        clearTimeout(closedPollTimeout);
        // win.closed threw — treating as closed
        clearLoginWindowOpened();
        window.location.reload();
      }
    }, 800);

    const closedPollTimeout = setTimeout(() => { clearInterval(closedPoll); }, 600000);
  }

  return { win, blocked: !win };
}

export default function ProtectedApp({ children }) {
  const auth = useAuth();
  const inPopup = isInPopupOrIframe();
  const isSsoWindow = isSsoLoginWindow();
  const signalSent = useRef(false);
  const [popupBlocked, setPopupBlocked] = React.useState(false);

  // Wire SSO token and 401 handler into apiClient — called in render body so
  // the token is always set before any child component's effects fire.
  configureApiClient(() => auth.user?.access_token);
  // On 401: try silent renewal first; behaviour differs by context.
  //
  // POPUP / IFRAME (Unity Link inside Opera Cloud flex link):
  //   NEVER call signinRedirect() here — it would navigate the whole iframe
  //   away from unity-link-dev.shrss.com to WSO2/Microsoft, after which Opera
  //   Cloud cancels the navigation and the re-load times out.
  //   Instead: clear the expired user so the existing "open login window"
  //   useEffect detects !isAuthenticated and fires a fresh small login window. ✅
  //
  // FULL TAB (standalone or new-tab):
  //   Save the current URL as returnTo BEFORE redirecting so the user lands
  //   back on the correct page (e.g. /point-adjustments?...) after SSO. ✅
  configureUnauthorizedHandler(async () => {
    if (inPopup && !isSsoWindow) {
      // In iframe/popup — silent-renew first; on failure clear the user so the
      // "open login window" flow in the auth useEffect re-authenticates smoothly.
      try {
        await auth.signinSilent();
      } catch (err) {
        // signinSilent failed in popup — clearing user for re-login
        await auth.removeUser();
      }
    } else {
      // Full-tab — save current path before redirect so returnTo is honoured
      try {
        await auth.signinSilent();
      } catch (err) {
        // signinSilent failed in full tab — redirecting to login
        const fullPath = window.location.pathname + window.location.search;
        if (!fullPath.startsWith('/logincallback')) {
          sessionStorage.setItem('returnTo', fullPath);
        }
        auth.signinRedirect();
      }
    }
  });

  // ── FIX: Login window already has active session ──────────────────────────
  // When login window opens with existing session → isAuthenticated=true
  // immediately. signinRedirect never fires. onSigninCallback never runs.
  // → no signal sent → popup never reloads :x:
  // FIX: detect this case here and signal popup directly :white_check_mark:
  useEffect(() => {
    if (
      isSsoWindow &&
      auth.isAuthenticated &&
      !auth.isLoading &&
      !signalSent.current
    ) {
      signalSent.current = true;

      // PRIMARY SIGNAL — postMessage user directly to iframe (the opener).
      // This bypasses Chrome storage partitioning: third-party iframes have
      // a different localStorage partition from top-level windows, so
      // localStorage/BroadcastChannel/storage-event all fail across the boundary.
      // postMessage uses direct window object references — no storage involved.
      try {
        if (window.opener && !window.opener.closed) {
          const userJson = JSON.parse(JSON.stringify(auth.user));
          window.opener.postMessage(
            { type: 'sso_user_data', user: userJson },
            window.location.origin
          );
        } else {
          sendSsoCompleteAndClose();
          return;
        }
      } catch (e) {
        sendSsoCompleteAndClose();
        return;
      }

      // Also send legacy signals as backup for same-origin (non-iframe) cases
      sendSsoCompleteAndClose();
    }
  }, [isSsoWindow, auth.isAuthenticated, auth.isLoading, auth.user]);

  // ── Main auth flow ────────────────────────────────────────────────────────
  useEffect(() => {
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.error &&
      !auth.activeNavigator
    ) {
      const fullPath = window.location.pathname + window.location.search;
      if (fullPath.startsWith('/logincallback')) return;

      // SSO LOGIN WINDOW — do SSO immediately
      // window.name = 'sso_login_window' survives all redirects :white_check_mark:
      if (isSsoWindow) {
        sessionStorage.setItem('returnTo', window.location.pathname + window.location.search);
        auth.signinRedirect();
        return;
      }

      // POPUP — open login window if not already open
      if (inPopup) {
        if (!isLoginWindowAlreadyOpen()) {
          const { blocked } = openLoginWindow(window.location.origin + window.location.pathname);
          if (blocked) setPopupBlocked(true);
        }
        return;
      }

      // NORMAL FULL TAB
      sessionStorage.setItem('returnTo', fullPath);
      auth.signinRedirect();
    }
  }, [
    auth.isLoading, auth.isAuthenticated, auth.error,
    auth.activeNavigator, auth, inPopup, isSsoWindow,
  ]);

  // ── Listeners: postMessage + storage event + BroadcastChannel + poll ────────
  // PRIMARY: postMessage — works across Chrome storage partitions (iframe ↔ top-level)
  // BACKUP: storage event, BroadcastChannel, poll — work for same-partition cases
  useEffect(() => {
    if (!inPopup || isSsoWindow) return;

    async function handleUserData(userJson) {
      try {
        // oidc-client-ts stores/reads the user from sessionStorage (by default)
        // using the key: `oidc.user:{authority}:{client_id}`
        // Writing directly here bypasses the need for auth.userManager entirely.
        // On reload, oidc-client-ts reads this key → isAuthenticated=true from start.
        const authority = authConfig.authority;
        const clientId = authConfig.client_id;
        const storageKey = `oidc.user:${authority}:${clientId}`;
        sessionStorage.setItem(storageKey, JSON.stringify(userJson));
        clearLoginWindowOpened();
        localStorage.removeItem('sso_complete');
        window.location.reload();
      } catch (e) {
        clearLoginWindowOpened();
        localStorage.removeItem('sso_complete');
        window.location.reload();
      }
    }

    function handleSsoComplete() {
      localStorage.removeItem('sso_complete');
      clearLoginWindowOpened();
      window.location.reload();
    }

    // LISTENER 1 — postMessage (PRIMARY — works when storage is partitioned)
    // The login window posts the serialized User object directly to this window.
    // Direct window reference avoids all storage partition boundaries.
    async function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'sso_user_data' && event.data?.user) {
        await handleUserData(event.data.user);
      }
    }
    window.addEventListener('message', onMessage);

    // LISTENER 2 — storage event (works when NOT partitioned / same-partition)
    function onStorageChange(event) {
      if (event.key === 'sso_complete' && event.newValue === 'true') {
        handleSsoComplete();
      }
    }
    window.addEventListener('storage', onStorageChange);

    // LISTENER 3 — BroadcastChannel
    let channel;
    try {
      channel = new BroadcastChannel('sso_channel');
      channel.onmessage = (event) => {
        if (event.data === 'sso_complete') {
          handleSsoComplete();
        }
      };
    } catch (e) {
      // BroadcastChannel setup failed (silently ignored)
    }

    // LISTENER 4 — localStorage poll (last-resort backup)
    const poll = setInterval(() => {
      try {
        if (localStorage.getItem('sso_complete') === 'true') {
          clearInterval(poll);
          handleSsoComplete();
        }
      } catch {
        clearInterval(poll);
      }
    }, 500);

    const timeout = setTimeout(() => clearInterval(poll), 300000);

    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorageChange);
      if (channel) channel.close();
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [inPopup, isSsoWindow]);

  // ── Silent renew error handler ────────────────────────────────────────────
  // automaticSilentRenew: true creates a hidden iframe to renew the token.
  // In a third-party iframe context (Unity Link inside Opera Cloud flex link),
  // Chrome blocks third-party cookies so the hidden renewal iframe fails.
  // oidc-client-ts fires silentRenewError in that case — intercept it here and
  // call removeUser() so the existing auth useEffect opens a fresh login window
  // BEFORE the token is actually used for an API call and a 401 occurs. ✅
  useEffect(() => {
    if (!auth.events) return;

    function onSilentRenewError(err) {
      if (inPopup && !isSsoWindow) {
        auth.removeUser();
      }
    }

    auth.events.addSilentRenewError(onSilentRenewError);
    return () => {
      auth.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [auth.events, auth, inPopup, isSsoWindow]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (auth.isLoading || auth.activeNavigator) {
    return (
      <div style={styles.centeredScreen}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>
          {auth.activeNavigator === 'signinRedirect' ? 'Completing sign in…' : 'Signing you in…'}
        </p>
      </div>
    );
  }

  // ── Auth Error ────────────────────────────────────────────────────────────
  if (auth.error) {
    return (
      <div style={styles.centeredScreen}>
        <p style={{ color: '#C0392B', fontWeight: 600 }}>
          Authentication error: {auth.error.message}
        </p>
        <button style={styles.retryBtn} onClick={() => {
          // Preserve the current URL so SSO returns to this page (e.g. /point-adjustments?...)
          // instead of the default home page.
          const fullPath = window.location.pathname + window.location.search;
          if (!fullPath.startsWith('/logincallback')) {
            sessionStorage.setItem('returnTo', fullPath);
          }
          auth.signinRedirect();
        }}>
          Retry Login
        </button>
      </div>
    );
  }

  // ── NOT authenticated + popup ─────────────────────────────────────────────
  if (!auth.isAuthenticated && inPopup && !isSsoWindow) {
    const handleOpenLogin = () => {
      clearLoginWindowOpened();
      setPopupBlocked(false);
      const { blocked } = openLoginWindow(window.location.origin + window.location.pathname);
      if (blocked) setPopupBlocked(true);
    };

    return (
      <div style={oc.page}>
        <div style={oc.card}>
          <img
            src="/assets/hardrock-logo.png"
            alt="Hard Rock"
            style={oc.logo}
            onError={e => { e.target.style.display = 'none'; }}
          />

          {popupBlocked ? (
            <>
              <div style={oc.blockedBanner}>
                <span style={oc.blockedIcon}>⚠️</span>
                <div>
                  <div style={oc.blockedTitle}>Sign-in window was blocked</div>
                  <div style={oc.blockedHint}>
                    Your browser blocked the login window. Click below
                    or allow pop-ups for this site in your browser settings.
                  </div>
                </div>
              </div>
              <button style={oc.primaryBtn} onClick={handleOpenLogin}>
                Open Sign-In Window
              </button>
            </>
          ) : (
            <>
              <div style={oc.spinner} />
              <p style={oc.title}>Waiting for sign-in…</p>
              <p style={oc.hint}>Complete sign-in in the login window.</p>
              <button style={oc.ghostBtn} onClick={handleOpenLogin}>
                Didn't open? Click here
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div style={styles.centeredScreen}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Redirecting to login…</p>
      </div>
    );
  }

  return children;
}

// \u2500\u2500 Existing styles (used by loading/error/redirect screens) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
const styles = {
  centeredScreen: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100vh', background: '#F4F5F7',
    fontFamily: 'Arial, sans-serif', gap: 12,
  },
  spinner: {
    width: 40, height: 40, border: '4px solid #DCE3EA',
    borderTop: '4px solid #0F3460', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: { color: '#555', fontSize: 14, margin: 0, textAlign: 'center', maxWidth: 260 },
  hintText: { fontSize: 11, color: '#888', marginTop: 4, textAlign: 'center', maxWidth: 260 },
  retryBtn: {
    marginTop: 8, padding: '10px 24px', border: 'none', borderRadius: 4,
    background: '#0F3460', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
};

// \u2500\u2500 Opera Cloud-aligned theme for the waiting-for-login screen \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Matches Opera Cloud's neutral palette: dark navy header, white card,
// Helvetica/Arial body font, teal accent buttons.
const oc = {
  page: {
    display: 'flex', flexDirection: 'column', height: '100vh',
    background: '#EAECEE', fontFamily: "'Helvetica Neue', Arial, sans-serif",
  },
  topBar: {
    background: '#1A2E44',         // Opera Cloud dark-navy header
    color: '#FFFFFF',
    padding: '10px 16px',
    fontSize: 13, fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  topBarTitle: { opacity: 0.9 },
  card: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '24px 20px', gap: 10,
  },
  logo: { width: 100, marginBottom: 8, objectFit: 'contain' },
  spinner: {
    width: 36, height: 36,
    border: '3px solid #D0D5DA',
    borderTop: '3px solid #1A6EBD',  // Opera Cloud teal-blue
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: 4,
  },
  title: {
    margin: 0, fontSize: 15, fontWeight: 600,
    color: '#1A2E44', textAlign: 'center',
  },
  hint: {
    margin: 0, fontSize: 12, color: '#6B7B8D',
    textAlign: 'center', maxWidth: 240, lineHeight: 1.4,
  },
  primaryBtn: {
    marginTop: 6,
    padding: '9px 20px',
    border: 'none', borderRadius: 3,
    background: '#1A6EBD',          // Opera Cloud primary blue
    color: '#fff', cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
    width: '100%', maxWidth: 240,
  },
  ghostBtn: {
    marginTop: 2,
    padding: '7px 16px',
    border: '1px solid #B0BAC4', borderRadius: 3,
    background: 'transparent',
    color: '#4A5568', cursor: 'pointer',
    fontSize: 12,
    width: '100%', maxWidth: 240,
  },
  blockedBanner: {
    display: 'flex', gap: 10, alignItems: 'flex-start',
    background: '#FFF8E1',
    border: '1px solid #F0C040',
    borderRadius: 4,
    padding: '10px 12px',
    maxWidth: 280, marginBottom: 8,
  },
  blockedIcon: { fontSize: 18, flexShrink: 0, lineHeight: 1.3 },
  blockedTitle: { fontSize: 13, fontWeight: 600, color: '#7A5000', marginBottom: 3 },
  blockedHint: { fontSize: 11, color: '#7A5000', lineHeight: 1.4 },
};

if (typeof document !== 'undefined' && !document.getElementById('__pa-spin-keyframe')) {
  const style = document.createElement('style');
  style.id = '__pa-spin-keyframe';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}